import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal, Button, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Split from 'react-split-it';
import { QRCodeSVG } from "qrcode.react";
import ListLines from "./ListLines";
import { getSectionPages } from "../../lib/bands/sectionPages";
import { getLayoutMode, getTemplateBands, getPageLayout, getTableMetrics, getItemRowHeight, getSetRowHeight, getTableBorderRadius, COLUMN_TOTAL_FIELD, formatColumnTotal, getPriceNotifyColours, LAYOUT_MODES, BAND_TYPES, BAND_LINE_HEIGHT, BAND_TABLE_LINE_HEIGHT, CURRENT_DATE_TIME_FIELD, formatDateTime } from "../../lib/bands/bandModel";
import { paginateBands } from "../../lib/bands/bandLayout";
import DetailRowLine from "./DetailRowLine";
import { applyCurrencyPrefix, isFlagOn, getPageNumberSettings, getPageNumberStyle, formatPageNumber, getShapeBorderStyle, getLineShapeStyle, getListSpacing, resolveTableCellStyle, tableCellCss, getUnderlineCellClass, getRowCellBackground, getColumnSampleText, getColumnFormat, isNumericColumnFormat, formatColumnValue, getBadgeSettings, findBadgeRule, resolveBadgeColors, getBadgeBoxStyle, getBandsPageNumberStyle, getTextBoxStyle } from "../../lib/global/helpers";

class SampleDataGenerator {
    static generate() {
        return {
            company: {
                name: "ACME Corporation",
                address: "123 Business Park Drive, Suite 100",
                locName: "San Francisco",
                locAddress: "123 street, SF",
                phone: "+1 (555) 123-4567",
                email: "billing@acmecorp.com",
                website: "www.acmecorp.com",
                vatNumber: "VAT-US-12345678",
                registrationNumber: "REG-987654321"
            },

            invoice: {
                number: "INV-2024-01234",
                date: new Date(2024, 5, 15),
                dueDate: new Date(2024, 6, 15),
                deliveryDate: new Date(2024, 5, 14),
                poNumber: "PO-7890123",
                terms: "Net 30",
                currency: "USD",
                currencySymbol: "$"
            },

            customer: {
                firstName: "Sam",
                lastName: "Smith",
                address: "456 Innovation Way, Floor 5",
                city: "Austin",
                state: "TX",
                zip: "78701",
                country: "USA",
                contact: "Sarah Johnson",
                email: "ap@techstart.io",
                phone: "+1 (512) 555-0123",
                vatNumber: "VAT-US-87654321",
                customerId: "CUST-2024-0567",
                EORINumber: "EORI-123"
            },

            items: [
                {
                    code: "DEV-001",
                    description: "Enterprise Software License - Annual",
                    quantity: 1,
                    unitPrice: 12000.00,
                    discount: 10,
                    tax: 20,
                    total: 10800.00,
                    packSize: 8
                },
                {
                    code: "CON-002",
                    description: "Implementation & Training Services",
                    quantity: 40,
                    unitPrice: 150.00,
                    discount: 5,
                    tax: 20,
                    total: 5700.00,
                    packSize: 2
                },
                {
                    code: "SUP-003",
                    description: "Premium Support Package - 12 months",
                    quantity: 1,
                    unitPrice: 3600.00,
                    discount: 0,
                    tax: 20,
                    total: 3600.00,
                    packSize: 5
                },
                {
                    code: "HRD-004",
                    description: "API Gateway Hardware Appliance",
                    quantity: 2,
                    unitPrice: 750.00,
                    discount: 0,
                    tax: 20,
                    total: 1500.00,
                    packSize: 3
                },
                {
                    code: "SVC-005",
                    description: "Data Migration Services",
                    quantity: 15,
                    unitPrice: 125.00,
                    discount: 0,
                    tax: 20,
                    total: 1875.00,
                    packSize: 4
                }
            ],

            totals: {
                subtotal: 23475.00,
                discountTotal: 1575.00,
                taxTotal: 4380.00,
                shipping: 50.00,
                grandTotal: 23905.00,
                paid: 0,
                due: 23905.00
            },

            vatSummary: {
                "20%": {
                    rate: 20,
                    taxableAmount: 21900.00,
                    taxAmount: 4380.00
                }
            },

            bankDetails: {
                bankName: "First International Bank",
                accountName: "ACME Corporation",
                accountNumber: "12345678901",
                sortCode: "12-34-56",
                iban: "GB29 NWBK 6016 1331 9268 19",
                swift: "NWBKGB2L"
            },

            notes: "Thank you for your business. Payment is due within 30 days.",
            termsAndConditions: "1. Goods remain property of ACME until paid in full.\n2. Late payments subject to 1.5% interest per month.\n3. All disputes must be submitted in writing within 14 days."
        };
    }

    static formatDate(date, format) {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');

        switch (format) {
            case "YYYY/MM/DD": return `${year}/${month}/${day}`;
            case "YYYY-MM-DD": return `${year}-${month}-${day}`;
            case "YYYY-MM-DD : HH:mm": return `${year}-${month}-${day} ${hours}:${minutes}`;
            case "YYYY-MM-DD : HH:mm:ss": return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            case "DD/MM/YYYY": return `${day}/${month}/${year}`;
            case "MM/DD/YYYY": return `${month}/${day}/${year}`;
            default: return `${year}-${month}-${day}`;
        }
    }

    static formatCurrency(amount, symbol) {
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);

        return symbol ? `${symbol}${formatted}` : formatted;
    }
}

class InvoicePreview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showPreview: false,
            sampleData: SampleDataGenerator.generate(),
            sampleRowCount: 5,
            // "Preview status": a colour-rule value to print in every element
            // that has a rule for it ("" = show the template as designed).
            badgePreviewValue: "",
            zoom: 1,
            splitSizes: [0.25, 0.55, 0.1, 0.1],
        };
    }

    componentDidMount() {
        const { heights } = this.props;
        const totalheight = heights.head + heights.body + heights.total + heights.foot;
        this.setState({
            splitSizes: [
                heights.head / totalheight,
                heights.body / totalheight,
                heights.total / totalheight,
                heights.foot / totalheight
            ]
        });
    }

    togglePreview = () => {
        this.setState({ showPreview: !this.state.showPreview });
    };

    // Every colour-rule value used in the template, first spelling kept, for
    // the "Preview status" selector.
    getBadgeRuleValues = () => {
        const { properties } = this.props;
        const elements = [
            ...['head', 'total', 'foot'].flatMap((section) => Object.values(properties[section]?.elements || {})),
            ...Object.values(properties.head?.companyProps || {}),
        ];
        const values = new Map();
        elements.forEach((element) => {
            getBadgeSettings(element).rules.forEach((rule) => {
                const value = (rule.value || '').toString().trim();
                if (value && !values.has(value.toLowerCase())) values.set(value.toLowerCase(), value);
            });
        });
        return [...values.values()];
    };

    // The value an element prints while a status is being previewed, or
    // undefined to print its normal text.
    getBadgePreviewValue = (element) => {
        const value = this.state.badgePreviewValue;
        return value && findBadgeRule(element, value) ? value : undefined;
    };

    previewBodyRef = React.createRef();

    static ZOOM_MIN = 0.25;
    static ZOOM_MAX = 2;
    static ZOOM_STEP = 0.1;

    setZoom = (zoom) => {
        const clamped = Math.min(InvoicePreview.ZOOM_MAX, Math.max(InvoicePreview.ZOOM_MIN, zoom));
        this.setState({ zoom: Math.round(clamped * 100) / 100 });
    };

    zoomIn = () => this.setZoom(this.state.zoom + InvoicePreview.ZOOM_STEP);

    zoomOut = () => this.setZoom(this.state.zoom - InvoicePreview.ZOOM_STEP);

    // Scales the page so its full width fits the visible preview area.
    zoomToFit = () => {
        const body = this.previewBodyRef.current;
        if (!body) return;
        const mmToPx = 96 / 25.4;
        const pageWidthPx = (this.props.properties.prop.orientation === "portrait" ? 210 : 297) * mmToPx;
        const padding = 40;
        this.setZoom((body.clientWidth - padding) / pageWidthPx);
    };

    resolvePropertyPath = (path, element) => {
        if (!path) return path;

        console.log("path: ", path)
        console.log("element: ", element)
        const { sampleData } = this.state;

        if (path === "CompanyName") return sampleData.company.name;
        if (path === "Address") return sampleData.company.address;
        if (path === "LocationName") return sampleData.company.locName;
        if (path === "LocationAddress") return sampleData.company.locAddress;
        if (path === "PhoneNumber") return sampleData.company.phone;
        if (path === "Email") return sampleData.company.email;
        if (path === "CompanyWeb") return sampleData.company.website;
        if (path === "VATRegistrationNo") return sampleData.company.vatNumber;
        if (path === "CompanyRegistrationNumber") return sampleData.company.registrationNumber;


        if (path === "InvoiceNumber") return sampleData.invoice.number;
        if (path === "InvoiceDate") {
            return SampleDataGenerator.formatDate(sampleData.invoice.date, element?.dateFormat || "YYYY-MM-DD");
        }
        if (path === "InvoiceDueDate") {
            return SampleDataGenerator.formatDate(sampleData.invoice.dueDate, element?.dateFormat || "YYYY-MM-DD");
        }
        if (path === "DeliveryDate") {
            return SampleDataGenerator.formatDate(sampleData.invoice.deliveryDate, element?.dateFormat || "YYYY-MM-DD");
        }
        if (path === "PONumber") return sampleData.invoice.poNumber;
        if (path === "Terms") return sampleData.invoice.terms;


        if (path === "CustomerFirstName") return sampleData.customer.firstName;
        if (path === "CustomerLastName") return sampleData.customer.lastName;
        if (path === "AddressLine1") return sampleData.customer.address;
        if (path === "City") return sampleData.customer.city;
        if (path === "State") return sampleData.customer.state;
        if (path === "Zip") return sampleData.customer.zip;
        if (path === "Country") return sampleData.customer.country;
        if (path === "CustomerContactNumber") return sampleData.customer.phone;
        if (path === "Email") return sampleData.customer.email;
        if (path === "VATNumber") return sampleData.customer.vatNumber;
        if (path === "ContactName") return sampleData.customer.contact;
        if (path === "CustomerID") return sampleData.customer.customerId;
        if (path === "EORINumber") return sampleData.customer.EORINumber;


        if (path === "BankName") return sampleData.bankDetails.bankName;
        if (path === "AccountName") return sampleData.bankDetails.accountName;
        if (path === "AccountNumber") return sampleData.bankDetails.accountNumber;
        if (path === "SortCode") return sampleData.bankDetails.sortCode;
        if (path === "IBAN") return sampleData.bankDetails.iban;
        if (path === "SwiftCode") return sampleData.bankDetails.swift;


        if (path === "VatSummary") {
            return "[VAT_SUMMARY_TABLE]";
        }


        return path;
    };

    // Sample rows for the preview, cycling the sample items so long invoices
    // (and the resulting page breaks) can be previewed.
    getSampleRows = (rowCount = this.state.sampleRowCount) => {
        const { sampleData } = this.state;
        const count = Math.max(0, parseInt(rowCount, 10) || 0);
        return Array.from({ length: count }, (_, i) => sampleData.items[i % sampleData.items.length]);
    };

    // Row/header heights the items table renders at; used to paginate.
    // Shared with the PDF backend (lib/bands/bandModel), so the table breaks
    // across pages in the same places.
    getTableMetrics = (detailRow = null) => getTableMetrics(this.props.properties.items.layout, detailRow);

    // Splits the invoice into pages the way the PDF should flow:
    // - the items table only takes the height its rows need and continues on
    //   new pages when it doesn't fit;
    // - the total section sits right under the last row, moving whole to the
    //   next page when it doesn't fit;
    // - the footer is pinned to the bottom of the last page (on its own page
    //   if there's no room left for it), or of every page when "Print Footer
    //   Every Page" is on — in which case every page keeps room for it.
    // The page breaks come from the band layout engine (lib/bands), which the
    // PDF backend is meant to follow too; the four sections are read as one
    // fixed band layout:
    // - the items table only takes the height its rows need and continues on
    //   new pages when it doesn't fit;
    // - the total section sits right under the last row, moving whole to the
    //   next page when it doesn't fit;
    // - the footer is pinned to the bottom of the last page (on its own page
    //   if there's no room left for it), or of every page when "Print Footer
    //   Every Page" is on — in which case every page keeps room for it.
    isBandsMode = () => getLayoutMode(this.props.properties) === LAYOUT_MODES.BANDS;

    // Bands mode: pages from the band layout engine, laid out in the content
    // area (inside the margins and page border). Blocks are in px from the
    // top of the content area. Text doesn't grow here — the preview shows
    // field names, not data — so bands keep their designed heights.
    getBandsPreviewPages = (rowCount) => {
        const { properties } = this.props;
        const rows = this.getSampleRows(rowCount);
        const { tableChrome } = this.getTableMetrics();
        const pageLayout = getPageLayout(properties);
        const bands = getTemplateBands(properties);
        const itemsBand = bands.find((band) => band.type === BAND_TYPES.ITEMS);
        const columns = Object.keys(properties.items.items).map((key) => properties.items.items[key]).filter((c) => c && c.width !== undefined);

        const rowLayout = this.getBandsRowLayout(rows, columns, pageLayout.content.width, itemsBand?.detailRow);
        const bandHeights = Object.fromEntries(bands.filter((band) => band.type !== BAND_TYPES.ITEMS).map((band) => [band.id, band.height]));
        const { pages } = paginateBands({
            bands,
            pageHeight: pageLayout.content.height,
            bandHeights,
            rowHeights: rowLayout.map((r) => r.total),
            tableChrome,
            emptyTopInset: 0,
        });

        return {
            bandsMode: true,
            pageLayout,
            bands,
            pages: pages.map((page) => ({
                blocks: page.blocks.map((block) => (block.rowCount === undefined ? block : {
                    ...block,
                    rows: rows.slice(block.firstRow, block.firstRow + block.rowCount),
                    rowLayout: rowLayout.slice(block.firstRow, block.firstRow + block.rowCount),
                })),
            })),
        };
    };

    getPreviewPages = (rowCount = this.state.sampleRowCount) => {
        if (this.isBandsMode()) return this.getBandsPreviewPages(rowCount);

        const { properties, heights } = this.props;
        const rows = this.getSampleRows(rowCount);
        const { tableChrome, rowPitch } = this.getTableMetrics();
        const mmToPx = 96 / 25.4;

        const layout = getSectionPages({
            template: { heights, head: properties.head, total: properties.total, foot: properties.foot },
            pageHeight: (properties.prop.orientation === "portrait" ? 297 : 210) * mmToPx,
            rowCount: rows.length,
            rowPitch,
            tableChrome,
            topMarginPx: (Number(properties.prop.margin?.top) || 0) * 96,
        });

        return {
            ...layout,
            pages: layout.pages.map((page) => ({
                ...page,
                rows: rows.slice(page.firstRowIndex, page.firstRowIndex + page.rowCount),
            })),
        };
    };

    // The sample text for one cell of an item row.
    // The sample row's raw value for a column, found the way the PDF finds it:
    // by the column's field (SalesUnitPrice, LineAmount, …), falling back to
    // its heading. null when the sample data has nothing that fits.
    getSampleCellValue = (item, column, rowIndex = 0) => {
        const field = String(column.data || String(column.name || '').split('.').pop() || '').toLowerCase();
        const header = String(column.header || '').toLowerCase();
        const has = (...words) => words.some((w) => field.includes(w) || header.includes(w));

        if (has('barcode', 'bar code')) return String(5012345678900 + rowIndex);
        if (has('code', 'sku', 'item#')) return item.code;
        if (has('packsize', 'pack size')) return item.packSize;
        if (has('price', 'rate')) return item.unitPrice;
        if (has('unittype', 'unit type') || field === 'unit' || header === 'unit') return ['EA', 'BOX', 'PACK'][rowIndex % 3];
        if (has('productname', 'description', 'product', 'service', 'name')) return item.description;
        if (has('quantity', 'qty')) return item.quantity;
        if (has('discount')) return item.discount;
        if (has('vat', 'tax')) return item.tax;
        if (has('weight')) return 0.5;
        if (has('total', 'amount')) return item.total;
        return null;
    };

    // A cell as the PDF prints it: number / currency / percentage columns use
    // their format (a number is made up if the sample has none, so the format
    // still shows); text columns print the value as it comes.
    getCellContent = (item, column, rowIndex = 0) => {
        const { sampleData } = this.state;
        const raw = this.getSampleCellValue(item, column, rowIndex);
        if (isNumericColumnFormat(getColumnFormat(column))) {
            return formatColumnValue(typeof raw === 'number' ? raw : 1234.5, column, sampleData.invoice.currencySymbol);
        }
        return raw === null || raw === undefined ? getColumnSampleText(column) : String(raw);
    };

    // A column total over the preview's sample rows, built by the same code
    // as the PDF (formatColumnTotal). Unit types follow the sample unit
    // column: EA, BOX, PACK in turn.
    getColumnTotalPreview = (element) => {
        const { properties } = this.props;
        const { sampleData } = this.state;
        const config = element.columnTotal || {};
        const column = properties.items.items?.[config.column] || { data: config.column, header: config.column };
        const numericColumn = isNumericColumnFormat(getColumnFormat(column));
        const unitField = config.unitField || "SaleUnitTypeName";
        const rows = this.getSampleRows().map((item, i) => {
            const raw = this.getSampleCellValue(item, column, i);
            return {
                [config.column]: typeof raw === "number" ? raw : numericColumn ? 1234.5 : 0,
                [unitField]: ["EA", "BOX", "PACK"][i % 3],
            };
        });
        const format = element.format && ["number", "currency", "percentage"].includes(element.format.type) ? element.format : null;
        return formatColumnTotal(rows, config, format
            ? (n) => formatColumnValue(n, { format }, sampleData.invoice.currencySymbol)
            : undefined);
    };

    // A sample value for a detail-line field (spec §6.2): pack size and unit
    // from the sample row, an empty brand on every other row so "leave out
    // fields with no value" shows, the field name otherwise.
    getDetailValue = (item, field, rowIndex) => {
        const key = (field || '').toLowerCase();
        if (key.includes('pack')) return item.packSize;
        if (key.includes('unit')) return ['EA', 'BOX', 'PACK'][rowIndex % 3];
        if (key.includes('brand')) return rowIndex % 2 ? '' : 'Brand 1';
        return `{${field}}`;
    };

    // Bands mode: each sample row's height in px, split into the row itself
    // (taller when a wrapping column needs more lines) and its detail line.
    // Pagination and drawing both use these, so pages break where they show.
    getBandsRowLayout = (rows, columns, tableWidth, detailRow) => {
        const { properties } = this.props;
        const metrics = this.getTableMetrics(detailRow);
        const fontPx = (Number(properties.items.layout.itemRow.fontSize) || 8) * (96 / 72);
        const totalWidth = columns.reduce((sum, c) => sum + Number(c.width || 10), 0) || 1;

        // The browser can't measure here before drawing, so the number of
        // lines a wrapped cell needs is estimated from its text length; the
        // PDF measures it. Everything else is the shared sizes.
        return rows.map((item, rowIndex) => {
            let lines = 1;
            columns.forEach((column) => {
                if (!column.wrap) return;
                const text = String(this.getCellContent(item, column, rowIndex) ?? '');
                const cellWidth = Math.max(10, tableWidth * Number(column.width || 10) / totalWidth - 20);
                const charsPerLine = Math.max(1, Math.floor(cellWidth / (fontPx * 0.55)));
                lines = Math.max(lines, Math.ceil(text.length / charsPerLine));
            });
            return {
                main: metrics.rowHeight + (lines - 1) * metrics.wrapLineHeight,
                detail: metrics.detailHeight,
                total: getItemRowHeight(metrics, lines),
            };
        });
    };

    // `options` (bands mode): { bandsMode, rowLayout, detailRow } — wrapping
    // columns, per-row heights and the detail line under each row.
    renderBodyElements = (rows = [], firstRowIndex = 0, options = {}) => {
        const { properties } = this.props;
        const { bandsMode = false, rowLayout = null, detailRow = null } = options;

        const displayData = Object.keys(properties.items.items)
            .map(key => properties.items.items[key])
            .filter(d => d && d.width !== undefined);

        const { headerHeight, rowHeight, rowPaddingY, headerBorderSize, rowLineSize } = this.getTableMetrics();
        const tableHeader = properties.items.layout.tableHeader;
        // A set row height centres one line of text (getTableMetrics).
        const setRowHeight = getSetRowHeight(properties.items.layout.itemRow);

        return (
            <div style={{
                border: `1px solid ${properties.items.layout.tableBorder}`,
                borderRadius: `${getTableBorderRadius(properties.items.layout)}px`,
                overflow: "hidden",
                fontFamily: properties.items.layout.itemRow.fontFamily || 'Inter',
                fontSize: `${properties.items.layout.itemRow.fontSize}pt`,
                color: properties.items.layout.itemRow.fontColor,
            }}>
                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: 'fixed'
                }}>
                    <thead>
                        <tr style={{ height: `${headerHeight}px` }}>
                            {displayData.map((obj, key) => (
                                <td
                                    key={`preview-h-${key}`}
                                    width={obj.width ? (obj.width / (displayData.reduce((a, v) => a + Number(v.width || 10), 0)) * 100 + "%") : 'auto'}
                                    className={getUnderlineCellClass(resolveTableCellStyle(properties.items.layout, obj, "header"))}
                                    style={{
                                        padding: "10px 10px 10px 10px",
                                        ...tableCellCss(resolveTableCellStyle(properties.items.layout, obj, "header")),
                                        borderBottom: tableHeader.showBottomBorder ? 'none' : `2px solid ${properties.items.layout.tableBorder}`,
                                        // One line, as getTableMetrics sizes the heading row
                                        // and the PDF draws it: a wrapped heading would make
                                        // the table taller than the space kept for it.
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {obj.header}
                                </td>
                            ))}
                        </tr>
                        {tableHeader.showBottomBorder && (
                            <tr>
                                <td colSpan={displayData.length} style={{ padding: 0 }}>
                                    <div style={{
                                        width: `${tableHeader.bottomBorderWidth ?? 100}%`,
                                        height: `${headerBorderSize}px`,
                                        backgroundColor: tableHeader.bottomBorderColor || '#333333ff'
                                    }} />
                                </td>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.map((item, i) => {
                            const rowIndex = firstRowIndex + i;
                            const layout = rowLayout ? rowLayout[i] : null;
                            const rowLine = properties.items.layout.itemRow.showHorizontalLines ?
                                `${properties.items.layout.itemRow.horizontalLineSize}px solid ${properties.items.layout.itemRow.horizontalLineColor}` :
                                'none';
                            // Every fifth sample row (the 3rd, 8th, …) has a changed price.
                            const notify = getPriceNotifyColours(properties.prop, { IsPriceNotify: rowIndex % 5 === 2 });
                            return (
                            <React.Fragment key={`preview-r-${rowIndex}`}>
                            <tr
                                style={{
                                    // With a detail line, the line under the row goes under the detail line instead.
                                    borderBottom: detailRow ? 'none' : rowLine,
                                    // The line under a row is drawn inside the row's
                                    // box (collapsed borders), so the box includes it:
                                    // rows then take the space getTableMetrics gives
                                    // them, as in the PDF.
                                    height: `${(layout ? layout.main : rowHeight) + (detailRow ? 0 : rowLineSize)}px`
                                }}
                            >
                                {displayData.map((column, colIndex) => {
                                    const content = this.getCellContent(item, column, rowIndex);
                                    const wraps = bandsMode && !!column.wrap;

                                    return (
                                        <td
                                            key={`preview-d-${rowIndex}-${colIndex}`}
                                            className={getUnderlineCellClass(resolveTableCellStyle(properties.items.layout, column, "row"))}
                                            style={{
                                                padding: `${rowPaddingY}px 10px`,
                                                ...tableCellCss(resolveTableCellStyle(properties.items.layout, column, "row")),
                                                backgroundColor: notify?.background || getRowCellBackground(properties.items.layout, column, rowIndex),
                                                ...(notify?.color ? { color: notify.color } : {}),
                                                borderRight: properties.items.layout.itemRow.showVerticleLines ?
                                                    `${properties.items.layout.itemRow.verticleLineSize}px solid ${properties.items.layout.itemRow.verticleLineColor}` :
                                                    'none',
                                                borderLeft: colIndex === 0 && properties.items.layout.itemRow.showVerticleLines ?
                                                    `${properties.items.layout.itemRow.verticleLineSize}px solid ${properties.items.layout.itemRow.verticleLineColor}` :
                                                    'none',
                                                whiteSpace: wraps ? 'normal' : 'nowrap',
                                                wordBreak: wraps ? 'break-word' : 'normal',
                                                ...(wraps || setRowHeight ? { lineHeight: BAND_TABLE_LINE_HEIGHT } : {}),
                                                verticalAlign: 'top',
                                                overflow: 'hidden',
                                                textOverflow: wraps ? 'clip' : 'ellipsis'
                                            }}
                                        >
                                            {content}
                                        </td>
                                    );
                                })}
                            </tr>
                            {detailRow && (
                                <tr style={{
                                    borderBottom: rowLine,
                                    height: `${(layout ? layout.detail : 0) + rowLineSize}px`,
                                    ...(notify?.background ? { backgroundColor: notify.background } : {}),
                                    ...(notify?.color ? { color: notify.color } : {}),
                                }}>
                                    <td colSpan={displayData.length} style={{ padding: '0 10px 4px 10px', verticalAlign: 'top' }}>
                                        <DetailRowLine detailRow={detailRow} valueOf={(field) => this.getDetailValue(item, field, rowIndex)} />
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };
    renderCompanyElements = () => {
        const { properties } = this.props;
        const { sampleData } = this.state;

        if (!properties.head?.companyProps) return null;

        return Object.keys(properties.head.companyProps).map(key => {
            const prop = properties.head.companyProps[key];
            if (!prop.show) return null;

            // const resolvedText = this.resolvePropertyPath(prop.data, prop);

            const defaultFontColor = properties.prop.fontColor;
            const defaultFontSize = `${properties.prop.fontSize}pt`;
            const sectionFont = properties.head?.font || {
                fontFamily: 'Inter',
                fontSize: properties.prop.fontSize,
                color: properties.prop.fontColor
            };

            const dStyle = getTextBoxStyle(prop.style);
            dStyle.fontFamily = prop.style?.fontFamily || sectionFont.fontFamily || 'Inter';
            if (!dStyle.color) {
                dStyle.color = sectionFont.color || defaultFontColor;
            }
            if (!dStyle.fontSize) {
                dStyle.fontSize = sectionFont.fontSize ? `${sectionFont.fontSize}pt` : defaultFontSize;
            }
            if (!dStyle.fontWeight) {
                dStyle.fontWeight = prop.style?.fontWeight || 'normal';
            }
            if (!dStyle.fontStyle) {
                dStyle.fontStyle = prop.style?.fontStyle || 'normal';
            }
            if (!dStyle.textDecoration) {
                dStyle.textDecoration = prop.style?.textDecoration || 'none';
            }
            if (!dStyle.textAlign) {
                dStyle.textAlign = prop.style?.textAlign || 'left';
            }

            const isList = dStyle.listStyleType && dStyle.listStyleType !== 'none';
            const badgeValue = this.getBadgePreviewValue(prop);
            const displayText = badgeValue ?? applyCurrencyPrefix(prop.text, prop, sampleData.invoice.currencySymbol);
            const badgeColors = resolveBadgeColors(prop, badgeValue ?? prop.text);
            if (badgeColors?.color) dStyle.color = badgeColors.color;
            const badgeBox = isList ? null : getBadgeBoxStyle(prop, badgeValue ?? prop.text);
            const textLines = displayText ? displayText.toString().split('\n') : [''];

            const content = isList ? (
                <ListLines
                    lines={textLines}
                    type={dStyle.listStyleType}
                    spacing={getListSpacing(prop)}
                    textStyle={{
                        color: dStyle.color,
                        fontSize: dStyle.fontSize,
                        lineHeight: dStyle.lineHeight,
                        fontFamily: dStyle.fontFamily,
                        fontWeight: dStyle.fontWeight,
                        fontStyle: dStyle.fontStyle,
                        textDecoration: dStyle.textDecoration,
                        textAlign: dStyle.textAlign
                    }}
                />
            ) : badgeBox ? (
                <span style={badgeBox}>{displayText}</span>
            ) : (
                displayText
            );

            return (
                <div
                    key={`preview-${prop.id}`}
                    style={dStyle}
                >
                    {prop.link ? (
                        <a
                            href={prop.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', font: 'inherit', textDecoration: dStyle.textDecoration }}
                        >
                            {content}
                        </a>
                    ) : content}
                </div>
            );
        });
    };

    renderElements = (section) => {
        const { properties } = this.props;
        const { sampleData } = this.state;

        if (!properties[section]?.elements) return null;

        return Object.keys(properties[section].elements).map(key => {
            const data = properties[section].elements[key];

            if (section === 'total' && (data.text === "[VAT_SUMMARY_TABLE]" || data.data === "VatSummary")) {
                return (
                    <div key={data.id} style={{ width: '100%' }}>
                        {this.renderVatSummaryTable()}
                    </div>
                );
            }

            // const resolvedText = this.resolvePropertyPath(data.data, data);

            const defaultFontColor = properties.prop.fontColor;
            const defaultFontSize = `${properties.prop.fontSize}pt`;
            const sectionFont = properties[section]?.font || {
                fontFamily: 'Inter',
                fontSize: properties.prop.fontSize,
                color: properties.prop.fontColor
            };

            const dStyle = getTextBoxStyle(data.style);
            dStyle.fontFamily = data.style?.fontFamily || sectionFont.fontFamily || 'Inter';
            if (!dStyle.color) {
                dStyle.color = sectionFont.color || defaultFontColor;
            }
            if (!dStyle.fontSize) {
                dStyle.fontSize = sectionFont.fontSize ? `${sectionFont.fontSize}pt` : defaultFontSize;
            }
            if (!dStyle.fontWeight) {
                dStyle.fontWeight = data.style?.fontWeight || 'normal';
            }
            if (!dStyle.fontStyle) {
                dStyle.fontStyle = data.style?.fontStyle || 'normal';
            }
            if (!dStyle.textDecoration) {
                dStyle.textDecoration = data.style?.textDecoration || 'none';
            }
            if (!dStyle.textAlign) {
                dStyle.textAlign = data.style?.textAlign || 'left';
            }

            const isList = dStyle.listStyleType && dStyle.listStyleType !== 'none';
            const badgeValue = this.getBadgePreviewValue(data);
            // The current date & time shows as it would print now.
            const displayText = data.data === CURRENT_DATE_TIME_FIELD
                ? formatDateTime(new Date(), data.format?.pattern || "DD/MM/YYYY HH:mm", data.timeZone)
                : data.data === COLUMN_TOTAL_FIELD
                    ? (data.text || "").split(`{${COLUMN_TOTAL_FIELD}}`).join(this.getColumnTotalPreview(data))
                    : badgeValue ?? applyCurrencyPrefix(data.text, data, sampleData.invoice.currencySymbol);
            const badgeColors = resolveBadgeColors(data, badgeValue ?? data.text);
            if (badgeColors?.color) dStyle.color = badgeColors.color;
            const badgeBox = isList ? null : getBadgeBoxStyle(data, badgeValue ?? data.text);
            // const textLines = resolvedText ? resolvedText.split('\n') : [''];
            const textLines = displayText ? displayText.split('\n') : [''];

            const content = isList ? (
                <ListLines
                    lines={textLines}
                    type={dStyle.listStyleType}
                    spacing={getListSpacing(data)}
                    textStyle={{
                        color: dStyle.color,
                        fontSize: dStyle.fontSize,
                        lineHeight: dStyle.lineHeight,
                        fontFamily: dStyle.fontFamily,
                        fontWeight: dStyle.fontWeight,
                        fontStyle: dStyle.fontStyle,
                        textDecoration: dStyle.textDecoration,
                        textAlign: dStyle.textAlign
                    }}
                />
            ) : badgeBox ? (
                <span style={badgeBox}>{displayText}</span>
            ) : (
                displayText
            );

            return (
                <div
                    key={data.id}
                    style={dStyle}
                >
                    {data.link ? (
                        <a
                            href={data.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', font: 'inherit', textDecoration: dStyle.textDecoration }}
                        >
                            {content}
                        </a>
                    ) : content}
                </div>
            );
        });
    };

    renderDocumentTitle = () => {
        const { properties } = this.props;

        if (!properties.head?.documentTitle?.show) return null;

        const title = properties.head.documentTitle;
        const defaultFontColor = properties.prop.fontColor;

        const dStyle = { ...title.style };
        if (!dStyle.color) {
            dStyle.color = defaultFontColor;
        }
        if (!dStyle.fontFamily && properties.head?.font?.fontFamily) {
            dStyle.fontFamily = properties.head.font.fontFamily;
        } else if (!dStyle.fontFamily) {
            dStyle.fontFamily = 'Inter';
        }
        if (!dStyle.fontSize) {
            dStyle.fontSize = "20pt";
        }
        if (!dStyle.fontWeight) {
            dStyle.fontWeight = title.style?.fontWeight || 'bold';
        }
        if (!dStyle.fontStyle) {
            dStyle.fontStyle = title.style?.fontStyle || 'normal';
        }
        if (!dStyle.textDecoration) {
            dStyle.textDecoration = title.style?.textDecoration || 'none';
        }
        if (!dStyle.textAlign) {
            dStyle.textAlign = title.style?.textAlign || 'center';
        }

        return (
            <div
                key={title.id}
                style={dStyle}
            >
                {title.title}
            </div>
        );
    };

    renderImage = (section) => {
        const { properties } = this.props;

        if (!properties.head?.logo || Object.keys(properties.head.logo).length === 0) return null;

        const logo = properties.head.logo;
        const logoSize = logo.logoSize || 100;
        const hasExplicitDimensions = logo.style?.width && logo.style?.height;

        const containerStyle = {
            ...logo.style,
            zIndex: 0,
            ...(!hasExplicitDimensions && { maxWidth: '100%', display: 'inline-block' }),
        };

        return logo.showLogo && logo.logo ? (
            <div style={containerStyle} key={logo.id}>
                <div style={{ width: '100%', height: '100%' }}>
                    <img
                        src={logo.logo}
                        style={{
                            width: hasExplicitDimensions ? "100%" : logoSize + "%",
                            height: hasExplicitDimensions ? "100%" : "auto",
                            objectFit: "fill",
                            pointerEvents: "none",
                            display: "block",
                        }}
                        alt="Company logo"
                    />
                </div>
            </div>
        ) : null;
    };

    renderShapes = (section) => {
        const { properties } = this.props;

        if (!properties[section]?.shapes || Object.keys(properties[section].shapes).length === 0) return null;

        return Object.keys(properties[section].shapes)
            .filter(s => s !== 'border')
            .map(key => {
                const data = properties[section].shapes[key];
                const font = properties[section]?.font || { fontFamily: 'Inter', fontSize: 10 };
                const bgColor = data.fillColor || properties[section].shapes.border.fillColor;
                const isLine = data.name && data.name.startsWith('line');

                const dStyle = isLine
                    ? { ...data.style, ...getLineShapeStyle(data, properties[section].shapes.border) }
                    : {
                        ...data.style,
                        minWidth: '32px',
                        minHeight: '16px',
                        ...getShapeBorderStyle(data, properties[section].shapes.border),
                        background: bgColor,
                        // zIndex: 0
                    };

                const shape = (
                    <div
                        key={data.link ? undefined : data.id}
                        style={{ ...dStyle, ...font }}
                    />
                );

                if (!data.link) return shape;

                return (
                    <a
                        key={data.id}
                        href={data.link}
                        aria-label={data.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...dStyle, ...font, display: 'block' }}
                    />
                );
            });
    };

    renderUploadedImages = (section) => {
        const images = this.props.properties[section]?.images;
        if (!images || Object.keys(images).length === 0) return null;

        return Object.keys(images).map(key => {
            const data = images[key];
            if (!data || !data.src) return null;

            return (
                <div key={data.id} style={{ ...data.style }}>
                    <img
                        src={data.src}
                        alt={data.alt || data.name}
                        style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                    />
                </div>
            );
        });
    };

    renderQRCodes = (section) => {
        const { properties } = this.props;

        if (!properties[section]?.qrCodes || Object.keys(properties[section].qrCodes).length === 0) return null;

        return Object.keys(properties[section].qrCodes).map(key => {
            const data = properties[section].qrCodes[key];
            const width = parseInt(data.style?.width, 10) || 80;
            const height = parseInt(data.style?.height, 10) || 80;
            const size = Math.max(1, Math.min(width, height));

            return (
                <div
                    key={data.id}
                    style={{
                        ...data.style,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff'
                    }}
                >
                    {data.qrMode === 'image' && data.qrImage ? (
                        <img src={data.qrImage} alt="QR code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : data.qrMode === 'generate' && data.qrValue ? (
                        <QRCodeSVG value={data.qrValue} size={size} style={{ width: '100%', height: '100%' }} />
                    ) : data.qrMode === 'field' && data.qrFieldName ? (
                        (() => {
                            const resolvedValue = this.resolvePropertyPath(data.qrFieldName, data);
                            return resolvedValue ? (
                                <QRCodeSVG value={resolvedValue.toString()} size={size} style={{ width: '100%', height: '100%' }} />
                            ) : null;
                        })()
                    ) : null}
                </div>
            );
        });
    };

    renderVatSummaryElement = () => {
        const { properties } = this.props;

        if (!properties.total?.VatSummary || Object.keys(properties.total.VatSummary).length === 0) return null;

        const data = properties.total.VatSummary;
        const defaultFontColor = properties.prop.fontColor;
        const defaultFontSize = `${properties.prop.fontSize}pt`;
        const sectionFont = properties.total?.font || {
            fontFamily: 'Inter',
            fontSize: properties.prop.fontSize,
            color: properties.prop.fontColor
        };

        const dStyle = { ...data.style };
        dStyle.width = '100%';
        dStyle.height = 'auto';
        dStyle.fontFamily = data.style?.fontFamily || sectionFont.fontFamily || 'Inter';

        if (!dStyle.color) {
            dStyle.color = sectionFont.color || defaultFontColor;
        }
        if (!dStyle.fontSize) {
            dStyle.fontSize = sectionFont.fontSize ? `${sectionFont.fontSize}pt` : defaultFontSize;
        }
        if (!dStyle.fontWeight) {
            dStyle.fontWeight = data.style?.fontWeight || 'normal';
        }
        if (!dStyle.fontStyle) {
            dStyle.fontStyle = data.style?.fontStyle || 'normal';
        }
        if (!dStyle.textDecoration) {
            dStyle.textDecoration = data.style?.textDecoration || 'none';
        }
        if (!dStyle.textAlign) {
            dStyle.textAlign = data.style?.textAlign || 'left';
        }

        if (data.text === "[VAT_SUMMARY_TABLE]" || data.data === "VatSummary") {
            return (
                <div key={data.id} style={dStyle}>
                    {this.renderVatSummaryTable()}
                </div>
            );
        }

        const isList = dStyle.listStyleType && dStyle.listStyleType !== 'none';
        const textLines = data.text ? data.text.split('\n') : [''];

        return (
            <div
                key={data.id}
                style={dStyle}
            >
                {isList ? (
                    <ListLines
                        lines={textLines}
                        type={dStyle.listStyleType}
                        spacing={getListSpacing(data)}
                        textStyle={{
                            color: dStyle.color,
                            fontSize: dStyle.fontSize,
                            fontFamily: dStyle.fontFamily,
                            fontWeight: dStyle.fontWeight,
                            fontStyle: dStyle.fontStyle,
                            textDecoration: dStyle.textDecoration,
                            textAlign: dStyle.textAlign
                        }}
                    />
                ) : (
                    data.text
                )}
            </div>
        );
    };

    renderVatSummaryTable = () => {
        const { properties } = this.props;

        const vatData = [
            { VatCode: 'S', VatPercentage: 20, GoodsValue: 15000.00, VatValue: 3000.00 },
            // { VatCode: 'Z', VatPercentage: 0, GoodsValue: 8475.00, VatValue: 0.00 },
            // { VatCode: 'E', VatPercentage: 5, GoodsValue: 5000.00, VatValue: 250.00 }
        ];

        const totalGoods = vatData.reduce((sum, item) => sum + item.GoodsValue, 0);
        const totalVat = vatData.reduce((sum, item) => sum + item.VatValue, 0);

        const totalSection = properties.total || {};
        const fontConfig = totalSection.font || {};
        const vatSummaryConfig = totalSection.VatSummary || {};
        const vatSummaryStyle = vatSummaryConfig.style || {};

        const fontSize = vatSummaryStyle.fontSize || fontConfig.fontSize || 10;
        const fontFamily = vatSummaryStyle.fontFamily || fontConfig.fontFamily || 'Inter';
        const color = vatSummaryStyle.color || fontConfig.color || '#333333';
        const backgroundColor = vatSummaryStyle.backgroundColor || '#ffffff';
        const headerBgColor = vatSummaryStyle.headerBackgroundColor || '#ffffff';
        const borderColor = vatSummaryStyle.borderColor || '#eeeeee';
        const headerPadding = "10pt"
        const rowHeight = fontSize + 8;
        const headerHeight = rowHeight + parseInt(headerPadding);

        const tableStyle = {
            width: '240pt',
            borderCollapse: 'collapse',
            fontFamily: fontFamily,
            fontSize: `${fontSize}pt`,
            color: color,
            backgroundColor: backgroundColor,
            border: `1px solid ${borderColor}`,
            marginTop: '10px',
            marginBottom: '10px'
        };

        const thStyle = {
            width: '25%',
            backgroundColor: headerBgColor,
            paddingLeft: '5px',
            height: headerHeight,
            textAlign: 'left',
            borderBottom: `2px solid ${borderColor}`,
            borderRight: `1px solid ${borderColor}`,
            fontWeight: 'bold'
        };

        const tdStyle = {
            width: '25%',
            height: rowHeight,
            padding: '0 5px',
            borderBottom: `1px solid ${borderColor}`,
            borderRight: `1px solid ${borderColor}`
        };

        return (
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Vat Code</th>
                        <th style={thStyle}>Vat Rate</th>
                        <th style={{ ...thStyle, textAlign: 'left', paddingRight: 0 }}>Good Values</th>
                        <th style={{ ...thStyle, textAlign: 'left', borderRight: 'none' }}>Vat</th>
                    </tr>
                </thead>
                <tbody>
                    {vatData.map((item, index) => (
                        <tr key={`vat-row-${index}`}>
                            <td style={tdStyle}>{item.VatCode}</td>
                            <td style={tdStyle}>{item.VatPercentage}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                {item.GoodsValue}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', borderRight: 'none' }}>
                                {item.VatValue}
                            </td>
                        </tr>
                    ))}
                    <tr style={{ backgroundColor: headerBgColor, fontWeight: 'bold' }}>
                        <td style={{ ...tdStyle, borderRight: `1px solid ${borderColor}`, fontWeight: 'bold' }}>
                            Totals
                        </td>
                        <td style={{ ...tdStyle, borderRight: `1px solid ${borderColor}`, fontWeight: 'bold' }}></td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>
                            {totalGoods}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', borderRight: 'none', fontWeight: 'bold' }}>
                            {totalVat}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    // One page in bands mode: frame, page number and the page's bands.
    renderBandsPreviewPage = (page, pageIndex, pageCount, layout) => {
        const { properties } = this.props;
        const { pageLayout, bands } = layout;
        const { content, frame, pageFrame } = pageLayout;
        const pageStyle = { width: pageLayout.page.width, height: pageLayout.page.height };
        const pageNumber = getPageNumberSettings(properties.prop);
        const place = (top, height) => ({ position: 'absolute', top: content.top + top, left: content.left, width: content.width, height, right: 'auto', bottom: 'auto' });

        return (
            <div key={`preview-page-${pageIndex}`} className="canves" style={{ ...pageStyle, margin: 0 }}>
                <div
                    className="canves-content"
                    style={{
                        backgroundColor: properties.prop.background.showColor ? properties.prop.background.color : "white",
                        ...pageStyle,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
                    }}
                >
                    {properties.prop.background.showImage && properties.prop.background.image && (
                        <div
                            className="background-image-layer"
                            style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundImage: `url(${properties.prop.background.image})`,
                                backgroundSize: properties.prop.background.imageSize ? `${properties.prop.background.imageSize}%` : 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                opacity: properties.prop.background.opacity || 1,
                                zIndex: 0,
                            }}
                        />
                    )}

                    {pageFrame && (
                        <div style={{
                            position: 'absolute', top: frame.top, left: frame.left, width: frame.width, height: frame.height, right: 'auto', bottom: 'auto',
                            boxSizing: 'border-box',
                            ...getShapeBorderStyle(pageFrame, null),
                            borderRadius: pageFrame.borderRadius,
                            zIndex: 0,
                        }} />
                    )}

                    {pageNumber.show && (
                        <div className="page-number" style={{ ...getBandsPageNumberStyle(properties.prop, properties.foot?.font, content, pageLayout.page), zIndex: 3, pointerEvents: 'none' }}>
                            {formatPageNumber(pageNumber.format, pageIndex + 1, pageCount)}
                        </div>
                    )}

                    {page.blocks.map((block) => {
                        const band = bands.find((b) => b.id === block.bandId);
                        if (!band) return null;

                        if (band.type === BAND_TYPES.ITEMS) {
                            return (
                                <div key={block.bandId} style={{ ...place(block.top, block.height), zIndex: 1 }}>
                                    {this.renderBodyElements(block.rows, block.firstRow, { bandsMode: true, rowLayout: block.rowLayout, detailRow: band.detailRow })}
                                </div>
                            );
                        }

                        const section = band.section;
                        const sectionContent = properties[section];
                        if (!sectionContent) return null;
                        // Content may sit in the page border's padding (spec
                        // §2.1): clip at the border's inner edge, not the
                        // padding — sideways always, up/down where the
                        // section touches the top or bottom of the content area.
                        const pad = pageFrame ? pageFrame.padding : 0;
                        const clipTop = block.top <= 0.5 ? pad : 0;
                        const clipBottom = block.top + block.height >= content.height - 0.5 ? pad : 0;
                        return (
                            <div
                                key={block.bandId}
                                style={{
                                    ...place(block.top, block.height),
                                    overflow: 'visible',
                                    clipPath: `inset(${-clipTop}px ${-pad}px ${-clipBottom}px ${-pad}px)`,
                                    zIndex: 1,
                                    lineHeight: BAND_LINE_HEIGHT,
                                    backgroundColor: sectionContent.background?.showColor ? sectionContent.background.color : 'transparent',
                                }}
                            >
                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                    {this.renderShapes(section)}
                                    {this.renderQRCodes(section)}
                                    {this.renderUploadedImages(section)}
                                    {section === 'head' && this.renderCompanyElements()}
                                    {this.renderElements(section)}
                                    {section === 'head' && this.renderImage('head')}
                                    {section === 'head' && this.renderDocumentTitle()}
                                    {section === 'total' && this.renderVatSummaryElement()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    renderPreviewPage = (page, pageIndex, pageCount, layout) => {
        if (layout.bandsMode) return this.renderBandsPreviewPage(page, pageIndex, pageCount, layout);
        const { properties } = this.props;
        const margin = properties.prop.margin;

        const pageStyle = properties.prop.orientation === "portrait"
            ? { width: '210mm', height: '297mm' }
            : { width: '297mm', height: '210mm' };

        return (
            <div
                key={`preview-page-${pageIndex}`}
                className="canves"
                style={{ ...pageStyle, margin: 0 }}
            >
                <div
                    className="canves-content"
                    style={{
                        backgroundColor: properties.prop.background.showColor
                            ? properties.prop.background.color
                            : "white",
                        width: pageStyle.width,
                        height: pageStyle.height,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
                    }}
                >
                    {properties.prop.background.showImage && properties.prop.background.image && (
                        <div
                            className="background-image-layer"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundImage: `url(${properties.prop.background.image})`,
                                backgroundSize: properties.prop.background.imageSize
                                    ? `${properties.prop.background.imageSize}%`
                                    : 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                opacity: properties.prop.background.opacity || 1,
                                zIndex: 0,
                            }}
                        />
                    )}

                    {/* Page number on every page, as set in Properties. */}
                    {getPageNumberSettings(properties.prop).show && (
                        <div
                            className="page-number"
                            style={{ ...getPageNumberStyle(properties.prop, properties.foot?.font), zIndex: 3, pointerEvents: 'none' }}
                        >
                            {formatPageNumber(getPageNumberSettings(properties.prop).format, pageIndex + 1, pageCount)}
                        </div>
                    )}

                    <div className="content-container" style={{ position: 'relative', zIndex: 1, height: '100%' }}>
                        <div id={pageIndex === 0 ? "templateContainer" : undefined} style={{ height: '100%' }}>
                            <div className="simple-vertical" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {page.showHeader ? (
                                    <div
                                        className="content canves-header"
                                        style={{
                                            height: `${layout.headPx}px`,
                                            flexShrink: 0,
                                            overflow: 'hidden',
                                            backgroundColor: properties.head.background.showColor
                                                ? properties.head.background.color
                                                : "transparent",
                                        }}
                                    >
                                        <div
                                            className="canves-content-children"
                                            style={{
                                                marginTop: margin ? `${margin.top}in` : '1in',
                                                marginLeft: margin ? `${margin.left}in` : '1in',
                                                marginRight: margin ? `${margin.right}in` : '1in',
                                                borderBottom: 0,
                                            }}
                                        >
                                            <div>
                                                {this.renderShapes("head")}
                                                {this.renderQRCodes("head")}
                                                {this.renderUploadedImages("head")}
                                                {this.renderCompanyElements()}
                                                {this.renderElements("head")}
                                                {this.renderImage("head")}
                                                {this.renderDocumentTitle()}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ height: `${page.topSpacer}px`, flexShrink: 0 }} />
                                )}

                                {page.hasTable && (
                                    <div
                                        className="content canves-body"
                                        style={{
                                            flexShrink: 0,
                                            position: 'relative',
                                            marginLeft: margin ? `${margin.left}in` : '1in',
                                            marginRight: margin ? `${margin.right}in` : '1in',
                                        }}
                                    >
                                        {this.renderBodyElements(page.rows, page.firstRowIndex)}
                                    </div>
                                )}

                                {/* "Above footer" mode: pushes the total down onto the footer. */}
                                {layout.totalAboveFooter && <div style={{ flex: '1 1 auto' }} />}

                                {page.showTotal && (
                                    <div
                                        className="content canves-total"
                                        style={{
                                            height: `${layout.totalPx}px`,
                                            flexShrink: 0,
                                            overflow: 'hidden',
                                            backgroundColor: properties.total.background.showColor
                                                ? properties.total.background.color
                                                : "transparent",
                                        }}
                                    >
                                        <div className="canves-content-children">
                                            <div style={{ position: "absolute" }}>
                                                {this.renderElements("total")}
                                                {this.renderVatSummaryElement()}
                                                {this.renderShapes("total")}
                                                {this.renderQRCodes("total")}
                                                {this.renderUploadedImages("total")}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Pushes the footer to the bottom of the page. */}
                                {!layout.totalAboveFooter && <div style={{ flex: '1 1 auto' }} />}

                                {page.showFooter && (
                                    <div
                                        className="content canves-footer"
                                        style={{
                                            height: `${layout.footPx}px`,
                                            flexShrink: 0,
                                            overflow: 'hidden',
                                            backgroundColor: properties.foot.background.showColor
                                                ? properties.foot.background.color
                                                : "transparent",
                                        }}
                                    >
                                        <div className="canves-content-children">
                                            <div style={{ position: "absolute" }}>
                                                {this.renderElements("foot")}
                                                {this.renderShapes("foot")}
                                                {this.renderQRCodes("foot")}
                                                {this.renderUploadedImages("foot")}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Picks a sample row count that makes the invoice exactly `pageCount`
    // pages long (the middle of that range, so the last page is part-filled),
    // falling back to the first count that reaches at least that many pages.
    getRowCountForPages = (pageCount) => {
        const MAX_ROWS = 500;
        let first = null;
        let last = null;
        let firstAtLeast = null;

        for (let n = 0; n <= MAX_ROWS; n++) {
            const pages = this.getPreviewPages(n).pages.length;
            if (pages === pageCount) {
                if (first === null) first = n;
                last = n;
            } else if (pages > pageCount) {
                if (firstAtLeast === null) firstAtLeast = n;
                break;
            }
        }

        if (first !== null) return Math.floor((first + last) / 2);
        return firstAtLeast !== null ? firstAtLeast : MAX_ROWS;
    };

    showPageCount = (pageCount) => {
        this.setState({ sampleRowCount: this.getRowCountForPages(pageCount) });
    };

    renderPreviewCanvas = () => {
        const layout = this.getPreviewPages();
        const pageCount = layout.pages.length;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {layout.pages.map((page, i) => (
                    <div key={`preview-page-wrap-${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {this.renderPreviewPage(page, i, pageCount, layout)}
                        <div style={{ height: '20px' }} />
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const previewPageCount = this.state.showPreview ? this.getPreviewPages().pages.length : 0;
        const badgeRuleValues = this.state.showPreview ? this.getBadgeRuleValues() : [];

        return (
            <>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={this.togglePreview}
                    title="Preview Invoice"
                >
                    <FontAwesomeIcon icon="eye" className="me-1" />
                    Preview
                </Button>

                <Modal
                    show={this.state.showPreview}
                    onHide={this.togglePreview}
                    scrollable
                    dialogClassName="preview-modal-dialog"
                    backdropClassName="preview-modal-backdrop"
                    style={{ zIndex: 10700 }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Invoice Preview</Modal.Title>
                        <div className="d-flex align-items-center ms-auto me-3" style={{ gap: '6px' }}>
                            <ButtonGroup size="sm">
                                <Button
                                    variant="outline-secondary"
                                    title="Zoom out"
                                    onClick={this.zoomOut}
                                    disabled={this.state.zoom <= InvoicePreview.ZOOM_MIN}
                                >
                                    <FontAwesomeIcon icon="search-minus" />
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    title="Reset to 100%"
                                    onClick={() => this.setZoom(1)}
                                    style={{ minWidth: '64px' }}
                                >
                                    {Math.round(this.state.zoom * 100)}%
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    title="Zoom in"
                                    onClick={this.zoomIn}
                                    disabled={this.state.zoom >= InvoicePreview.ZOOM_MAX}
                                >
                                    <FontAwesomeIcon icon="search-plus" />
                                </Button>
                            </ButtonGroup>
                            <Button size="sm" variant="outline-secondary" title="Fit page width" onClick={this.zoomToFit}>
                                <FontAwesomeIcon icon="expand-arrows-alt" className="me-1" />
                                Fit
                            </Button>
                        </div>
                    </Modal.Header>
                    <Modal.Body ref={this.previewBodyRef} style={{ backgroundColor: '#f5f5f5', padding: '20px' }}>
                        <div
                            className="preview-wrapper"
                            style={{
                                width: 'fit-content',
                                margin: '0 auto',
                                zoom: this.state.zoom,
                            }}
                        >
                            {this.renderPreviewCanvas()}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="text-muted me-auto d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                            <small>Preview as</small>
                            <ButtonGroup size="sm">
                                {[1, 2, 3].map((count) => (
                                    <Button
                                        key={`page-count-${count}`}
                                        variant={previewPageCount === count ? "primary" : "outline-primary"}
                                        onClick={() => this.showPageCount(count)}
                                    >
                                        {count === 1 ? "1 page" : `${count} pages`}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <small className="ms-2">Sample item rows</small>
                            <input
                                type="number"
                                min="0"
                                max="500"
                                className="form-control form-control-sm"
                                style={{ width: '80px' }}
                                value={this.state.sampleRowCount}
                                onChange={(e) => this.setState({ sampleRowCount: Math.min(500, Math.max(0, parseInt(e.target.value, 10) || 0)) })}
                            />
                            {badgeRuleValues.length > 0 && (
                                <>
                                    <small className="ms-2">Preview status</small>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: 'auto' }}
                                        title="Print this value in every element that has a colour rule for it"
                                        value={badgeRuleValues.includes(this.state.badgePreviewValue) ? this.state.badgePreviewValue : ''}
                                        onChange={(e) => this.setState({ badgePreviewValue: e.target.value })}
                                    >
                                        <option value="">As designed</option>
                                        {badgeRuleValues.map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                            <small>
                                {this.isBandsMode()
                                    ? `${previewPageCount} ${previewPageCount === 1 ? "page" : "pages"} — bands print by their own rules (Bands tab); field names show instead of data, so bands that grow keep their designed height here.`
                                    : <>{previewPageCount} {previewPageCount === 1 ? "page" : "pages"} — the total {this.props.properties.total?.position === "aboveFooter" ? "sits directly above the footer" : "follows the last row"}; the footer sits at the bottom of {isFlagOn(this.props.properties.foot?.printingFooterEveryPage) ? "every page" : "the last page"}.</>}
                            </small>
                        </div>
                        <Button variant="secondary" onClick={this.togglePreview}>
                            Close Preview
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (state) => {
    const template = state.template;
    // Content of added bands, under their own keys like the four sections.
    const bandSections = {};
    (template.bands || []).forEach((band) => {
        if (band?.section && template[band.section]) bandSections[band.section] = template[band.section];
    });
    return {
        properties: {
            ...bandSections,
            prop: template.prop,
            head: template.head,
            items: template.items,
            total: template.total,
            foot: template.foot,
            bands: template.bands,
            pageFrame: template.pageFrame,
        },
        heights: template.heights
    };
};

export default connect(mapStateToProps, null)(InvoicePreview);